// App.jsx
import React, { useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { hasAnalyticsConsent, loadGoogleAnalytics, logPageView } from './analytics/googleAnalytics';
import { loadMetaPixel, metaPageView } from './analytics/metaPixel';
import { consumeLandingPath } from './analytics/campaign';
import ScrollToTop from "./components/ScrollToTop";
import SEOLayout from "./components/SEOLayout";
import CookieConsentModal from "./components/CookieConsentModal";
import CampanaBar from "./components/CampanaBar";
import Landing from "./pages/Landing";
import EmbajadoresLanding from "./pages/EmbajadoresLanding";
import CoachLandingPage from "./components/coach/CoachLandingPage";
import CoachTerms from "./coach/CoachTerms";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import EliminarCuenta from "./pages/EliminarCuenta";
import Contact from "./pages/Contact";
import Help from "./pages/Help";
import PublicCoachProfile from "./pages/PublicCoachProfile";
import AlternativeMyFitnessPal from "./pages/AlternativeMyFitnessPal";
import AlternativaHarbiz from "./pages/AlternativaHarbiz";
import CaloriesPhotoApp from "./pages/CaloriesPhotoApp";
import MacrosApp from "./pages/MacrosApp";
import DownloadRedirect from "./pages/DownloadRedirect";
import ProductCategoryDropdown from "./pages/test";
// Paneles privados: se cargan solo al entrar en /admin o /coach. Ningun
// visitante ni buscador los pisa, y se llevan ~1,2 MB fuera del bundle
// inicial (admin/ + coach/ + quill + chart.js).
const AdminApp = lazy(() => import("./admin/AdminApp"));
const CoachApp = lazy(() => import("./coach/CoachApp"));
import Blog from './pages/Blog';
import BlogPost from './pages/Blogpost';
import CoachBlog from './pages/CoachBlog';
import CoachBlogPost from './pages/CoachBlogPost';
import TestMetabolismo from './pages/TestMetabolismo';
import Cookies from "./pages/Cookies"; 
import NotFound from './pages/NotFound';
import CalorieCalculator from './pages/CalorieCalculator';
import Premium from './pages/Premium';
import Referido from './pages/Referido';
import PagoExitoso from './pages/PagoExitoso';
import ActivarCuenta from './pages/ActivarCuenta';
import { HelmetProvider } from 'react-helmet-async';


import "./App.css";

function AltaCoachAntigua() {
  const { search } = useLocation();
  return <Navigate to={`/coach/register${search}`} replace />;
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    if (hasAnalyticsConsent()) {
      loadGoogleAnalytics();
      loadMetaPixel();
    }
  }, []);

  // Track cada cambio de página
  useEffect(() => {
    logPageView(location.pathname + location.search);
    metaPageView();
  }, [location.pathname, location.search]);

  const handleCookieConsent = (value) => {
    if (value === "accepted") {
      // Si el visitante entró con utm_* y ya ha navegado mientras decidía sobre las
      // cookies, el primer page_view debe llevar la ruta de ENTRADA, no la actual:
      // es lo que permite a GA4 atribuir la sesión a la campaña en vez de a "directo".
      const path = consumeLandingPath() || location.pathname + location.search;
      logPageView(path);
      metaPageView();
    }
  };

  return (
    <HelmetProvider>
      <ScrollToTop />
      <SEOLayout>
        {/* La barra de campaña se monta UNA vez, encima de todas las rutas
            públicas. Antes vivía dentro de Landing y solo la veía quien entraba
            por la portada; por /contador-calorias, las landings de SEO y los
            posts del blog -por donde entra la mayoría desde Google- no se
            anunciaba nada. Ella misma se calla en los paneles privados. */}
        <CampanaBar />
        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/embajadores" element={<EmbajadoresLanding />} />
          <Route path="/coaches" element={<CoachLandingPage />} />
          <Route path="/alternativa-myfitnesspal" element={<AlternativeMyFitnessPal />} />
          <Route path="/alternativa-harbiz" element={<AlternativaHarbiz />} />
          <Route path="/app-para-contar-calorias-con-foto" element={<CaloriesPhotoApp />} />
          <Route path="/app-para-contar-macros" element={<MacrosApp />} />
          {/* 24-sep: juntadas en /coaches (el 301 de verdad lo da nginx). */}
          <Route path="/software-para-entrenadores-personales" element={<Navigate to="/coaches" replace />} />
          <Route path="/software-para-coaches-online" element={<Navigate to="/coaches" replace />} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/coaches/:coachId" element={<PublicCoachProfile />} />
          {/* /coach-register NO es la ruta del alta (es /coach/register), pero ya
              salió en correos a entrenadores: el 26-sep a un lead de la captación y
              en el aviso de CoachDesdeAppMail. Se redirige conservando ?email=. */}
          <Route path="/coach-register" element={<AltaCoachAntigua />} />
          <Route path="/coach/terminos" element={<CoachTerms />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          <Route path="/test" element={<ProductCategoryDropdown />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/coach/*" element={<CoachApp />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/blog-entrenadores" element={<CoachBlog />} />
          <Route path="/blog-entrenadores/:slug" element={<CoachBlogPost />} />
          <Route path="/test-metabolismo" element={<TestMetabolismo />} />
          <Route path="/contador-calorias" element={<CalorieCalculator />} />
          <Route path="/premium" element={<Premium />} />
          {/* El enlace que reparte cada creador. noindex: ver Referido.jsx. */}
          <Route path="/r/:codigo" element={<Referido />} />
          <Route path="/pago-exitoso" element={<PagoExitoso />} />
          <Route path="/activar-cuenta" element={<ActivarCuenta />} />
          <Route path="/cookies" element={<Cookies />} />
          {/* La exige Google Play: una URL publica con los pasos para borrar la cuenta. */}
          <Route path="/eliminar-cuenta" element={<EliminarCuenta />} />
          <Route path="*" element={<NotFound />} /> {/* Página 404 para rutas no encontradas */}
        </Routes>
        </Suspense>
      </SEOLayout>
      <CookieConsentModal onConsentChange={handleCookieConsent} />
    </HelmetProvider>
  );
}
